import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, Share, MoreVertical, PlusSquare } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua));

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 pt-32 pb-20 max-w-2xl text-center">
        <Smartphone className="mx-auto mb-6 text-primary" size={48} />
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-foreground">
          Install Paradise Beauty
        </h1>
        <p className="text-muted-foreground mb-10 text-lg">
          Add the app to your home screen for quick access to bookings, services, and your account.
        </p>

        {isInstalled ? (
          <div className="bg-primary/10 rounded-2xl p-8 border border-primary/20">
            <p className="text-primary font-semibold text-lg">✓ App is already installed!</p>
            <p className="text-muted-foreground mt-2">Open it from your home screen.</p>
          </div>
        ) : deferredPrompt ? (
          <Button onClick={handleInstall} size="lg" className="rounded-full px-10 text-base">
            <Download className="mr-2" size={18} />
            Install App
          </Button>
        ) : isIOS ? (
          <div className="bg-card rounded-2xl p-8 border border-border space-y-6 text-left">
            <h2 className="font-serif text-xl font-semibold text-foreground text-center">
              Install on iPhone / iPad
            </h2>
            <ol className="space-y-4 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">1</span>
                <span>Tap the <Share className="inline mx-1" size={16} /> <strong>Share</strong> button in Safari's toolbar.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">2</span>
                <span>Scroll down and tap <PlusSquare className="inline mx-1" size={16} /> <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">3</span>
                <span>Tap <strong>Add</strong> to confirm.</span>
              </li>
            </ol>
          </div>
        ) : (
          <div className="bg-card rounded-2xl p-8 border border-border space-y-6 text-left">
            <h2 className="font-serif text-xl font-semibold text-foreground text-center">
              Install on Android
            </h2>
            <ol className="space-y-4 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">1</span>
                <span>Tap the <MoreVertical className="inline mx-1" size={16} /> <strong>menu</strong> button in Chrome.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">2</span>
                <span>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shrink-0">3</span>
                <span>Tap <strong>Install</strong> to confirm.</span>
              </li>
            </ol>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Install;
